import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Count from "./Count";
import AlarmFilters from "./Filters";
import List from "./List";
import Timeline from "./Timeline";
import RaisesChart from "./Timeline/RaisesChart";
import Dragable from "components/Dragable";
import Tooltip from "@mui/material/Tooltip";
import Fab from "@mui/material/Fab";
import Zoom from "@mui/material/Zoom";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import TimelineIcon from "@mui/icons-material/Timeline";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import Reports from "./Reports";
import { useSnackbar } from "notistack";
import { useLocation, Redirect } from "react-router-dom";
import { setAlarmsFilter, updateAlarmsFilter } from "rtkSlices/AlarmsFilterSlice";
import { setMapPage, setListPage } from "rtkSlices/AssetViewSlice";
import { resetFilter } from "rtkSlices/filterDevicesSlice";

export default function AlarmsDashboard(props) {
  const location=useLocation()
  let priorities = ["CRITICAL", "MAJOR", "MINOR", "WARNING"];
  let statuses = ["ACTIVE", "ACKNOWLEDGED", "CLEARED"];
  const metaDataValue = useSelector((state) => state.metaData);
  const alarmsFilter = useSelector((state) => state.alarmsFilter);
  // Scheduled-Reports gate. ⊤ (super-admin) always sees it; otherwise the
  // permission comes from the Solution Features > Scheduled Reports master
  // (undefined => not granted => FAB + Drawer hidden entirely). Two-level
  // optional chaining tolerates a role with no Solution Features row at all.
  const schedReportsPerm = metaDataValue.unscopedAdmin
    ? "ALL"
    : metaDataValue.modulePermissions?.["Solution Features"]?.["Scheduled Reports"];
  // Page gate: the alarms dashboard belongs to the Solution Features > Alarms
  // master. Non-holders are sent back to the catalogue (never a blank page).
  // Only gate once the role has actually loaded (apps is undefined until then).
  const holdsAlarmsFeature =
    metaDataValue.unscopedAdmin ||
    ["ALL", "READ"].includes(
      metaDataValue.modulePermissions?.["Solution Features"]?.Alarms
    );
  const roleLoaded = !!metaDataValue.apps;
  // console.log({alarmsFilter})
  const [csvData, setCsvData] = React.useState([]);
  const [allAlarms, setAllAlarms] = React.useState([]);
  const [drawer, setDrawer] = React.useState(false);
  const [view, setView] = React.useState("list");
  const dispatch=useDispatch()

  // Resolve the "originating" solution context once. Two consumers:
  //   1. mount effect — seed the filter dropdown's default
  //   2. Filters back button — where "close" returns to
  // ESB writes a group id (not service id) into localStorage.currentService;
  // the group branch preserves back-nav for those users but falls back to all
  // solutions ([]) for the dropdown since a group spans multiple services.
  const origin = useMemo(() => {
    const fromState = location.state?.selectedSolution;
    if (fromState?.id && metaDataValue.services.find((s) => s.id === fromState.id)) {
      return { name: fromState.name, backUrl: `/solutions/${fromState.id}` };
    }
    const ls = window.localStorage.getItem("currentService");
    if (ls) {
      const svc = metaDataValue.services.find((s) => s.id === ls);
      if (svc) {
        return { name: svc.name, backUrl: `/solutions/${svc.id}` };
      }
      if (metaDataValue.services.some((s) => s.group?.id === ls)) {
        return { name: null, backUrl: `/solutions/${ls}` };
      }
    }
    return null;
  }, [location.state, metaDataValue.services]);

  useEffect(() => {
    dispatch(updateAlarmsFilter({
      solutions: origin?.name ? [origin.name] : [],
    }));
  }, [origin?.name]);

  // Stale-selection guard. Solution names no longer in metaDataValue.services
  // (deleted/renamed mid-session, or a stale URL/state) are dropped from the
  // selection so the dashboard scope matches the UI. alarmsQuery skips
  // unknown names too, so the request stays RBAC-scoped either way.
  useEffect(() => {
    if (!metaDataValue.services?.length) return;
    const gone = alarmsFilter.solutions.filter(
      (name) => !metaDataValue.services.some((s) => s.name === name)
    );
    if (gone.length) {
      dispatch(updateAlarmsFilter({
        solutions: alarmsFilter.solutions.filter((name) => !gone.includes(name)),
      }));
      enqueueSnackbar(
        {
          title: "Alarms",
          message: `No longer accessible, removed from the filter: ${gone.join(", ")}`,
          variant: "warning",
        },
        { timeOut: 3000 }
      );
    }
  }, [alarmsFilter.solutions, metaDataValue.services]);

  useEffect(()=>{
    // The default range is the 7 days up to now each time the page opens: the
    // slice's initial range is worked out when the app loads and the reset
    // below when the page closes (code review 2026-10-30).
    const now = new Date();
    dispatch(updateAlarmsFilter({
      date: {
        startTime: new Date(now).setDate(now.getDate() - 7),
        endTime: now.getTime(),
      },
    }));

    //resetting form on page unmount

    const reset=()=>{
      dispatch(setAlarmsFilter({
        date: {
          startTime: new Date().setDate(new Date().getDate() - 7),
          endTime: new Date().setDate(new Date().getDate())
        },
        status: ["ACTIVE","ACKNOWLEDGED"],
        solutions: [],
        priority: [],
        emails: false,
        actuations: false,
        search: {
          text: "",
          by: ["asset", "rule"]
        }
      }))
    }


    return ()=>{
      reset()
      // the device list page and filters the ESB and Energy Management pages
      // read; moved here from the old Trend card (removed 2026-10-30), reset once when the page closes
      dispatch(setMapPage(1));
      dispatch(setListPage(1));
      dispatch(resetFilter());
    }
  },[])



  const { enqueueSnackbar } = useSnackbar();

  function generateRows(data) {
    // Guard against an absent payload. When the alarms query returns an
    // empty/short-shaped payload (e.g. no devices matched the filter),
    // `data` can be undefined; `JSON.stringify(undefined)` yields the value
    // `undefined`, and `JSON.parse(undefined)` throws
    // `SyntaxError: "undefined" is not valid JSON`.
    if (!Array.isArray(data)) return [];
    let tempData = JSON.parse(JSON.stringify(data));
    tempData.forEach((r) => {
      // the alarm's own solution (several can be picked)
      r.service = metaDataValue.services.find((s) => s.id == r.serviceId)?.name;
      // Leave createdAt/updatedAt as the raw ISO value here. The List table
      // renders every cell through newTable.js `sortDate`, which parses the
      // value and formats it en-GB for display — pre-formatting to a
      // DD/MM/YYYY string here would make that `new Date(...)` mis-parse it as
      // US M/D/YYYY (day/month swap). The CSV export formats these fields
      // explicitly in generateExportData below.
      if (isJsonString(r.text) != r.text) {
        r.text = isJsonString(r.text);
        // Prefer the explicit `reason` field on the parsed payload when
        // present (e.g. geofence entry/exit alarms where the human text
        // sits there and there's no sensor/reading). Fall back to the
        // assembled "<sensor> (value: X) is <condition> - <threshold>"
        // shape, but skip each segment whose inputs are undefined so
        // we never produce "(value: undefined)" or " - undefined" noise.
        const hasReading =
          r.text?.sensorFriendlyName !== undefined &&
          r.text?.sensorFriendlyName !== null &&
          r.text?.reading !== undefined &&
          r.text?.reading !== null;
        const readingSegment = hasReading
          ? `${r.text.sensorFriendlyName} (value: ${r.text.reading})`
          : "";
        const conditionSegment =
          r.text?.condition !== undefined && r.text?.condition !== null
            ? `${readingSegment ? " is " : ""}${r.text.condition}`
            : "";
        const thresholdSegment =
          r.text?.threshold !== undefined && r.text?.threshold !== null
            ? ` - ${r.text.threshold}`
            : "";
        const assembled = (readingSegment + conditionSegment + thresholdSegment).trim();
        r.reason = r.text.reason || assembled || "No details available";
        r.email = r.text.emails;
        if (r.text.actions && r.text.actions.length && r.text.actuatorNames) {
          r.actuations = [];
          r.text.actions.forEach((a) => {
            r.actuations.push(
              `${r.text.actuatorNames[a.actuatorId]} → ${a.commandLabel}`
            );
          });
        }
      } else {
        r.reason = r.text;
      }
    });
    return tempData;
  }

  function generateExportData(data) {
    if (!Array.isArray(data)) {
      setCsvData([]);
      return;
    }
    let tempColumns = [
      { id: "service", label: "Solution", align: "center" },
      { id: "deviceName", label: "Asset", align: "center" },
      { id: "severity", label: "Priority", align: "center" },
      { id: "status", label: "Status", align: "center" },
      {
        // "Count" is the alarm's LIFETIME repeat counter (since Created At),
        // maintained by Cumulocity dedup on every re-raise. It is NOT scoped
        // to the dashboard's date filter - label it so a week-filtered view
        // isn't read as "N occurrences this week".
        id: "count", label: "Count (since created)", align: "center" },
      { id: "reason", label: "Reason", align: "center" },
      { id: "email", label: "Emails", align: "center" },
      { id: "actuations", label: "Actuations", align: "center" },
      { id: "type", label: "Type", align: "center" },
      { id: "createdAt", label: "Created At", align: "center" },
      { id: "updatedAt", label: "Updated At", align: "center" },
    ];
    let keys = tempColumns.map((c) => c.id);
    data.forEach((t) => {
      Object.keys(t).forEach((t2) => {
        if (!keys.includes(t2)) {
          delete t[t2];
        }
      });
    });
    // Fields whose value is a raw ISO date and must be rendered en-GB
    // (DD/MM/YYYY HH:mm:ss) in the export — matching what the List shows and
    // avoiding the browser-locale-dependent US M/D/YYYY.
    const dateFields = ["createdAt", "updatedAt"];
    const fmtCsv = (id, val) => {
      if (!dateFields.includes(id) || !val) return val;
      const dt = new Date(val);
      return isNaN(dt)
        ? val
        : `${dt.toLocaleDateString("en-GB")} ${dt.toLocaleTimeString("en-GB")}`;
    };
    let tempCsvData = [];
    tempCsvData.push(tempColumns.map((t) => t.label));
    data.forEach((t1, i) => {
      tempCsvData[0].forEach((t2, j) => {
        if (!tempCsvData[i + 1]) {
          tempCsvData[i + 1] = [];
        }
        const col = tempColumns.find((c) => c.label == t2);
        tempCsvData[i + 1].push(fmtCsv(col.id, t1[col.id]));
      });
    });
    setCsvData(tempCsvData);
  }

  function isJsonString(str) {
    let out;
    try {
      out = JSON.parse(str);
    } catch (e) {
      return str;
    }
    return out;
  }

  const priorityChart = () => {
    return (
      <Count
        type="priorities"
        setAllAlarms={setAllAlarms}
        generateRows={generateRows}
        generateExportData={generateExportData}
        serviceId={props.serviceId}
      />
    );
  };

  const statusChart = () => {
    return (
      <Count
        type="status"
        setAllAlarms={setAllAlarms}
        generateRows={generateRows}
        generateExportData={generateExportData}
        serviceId={props.serviceId}
      />
    );
  };

  const toggleDrawer = () => {
    setDrawer(!drawer);
  };

  // List / Timeline switch, shown in the header of whichever card is open
  // (chip pattern from Solution Insights).
  const viewToggle = (
    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
      {[
        { id: "list", icon: FormatListBulletedIcon, label: "List" },
        { id: "timeline", icon: TimelineIcon, label: "Timeline" },
      ].map((e) => (
        <Chip
          key={e.id}
          size="small"
          label={e.label}
          id={e.label}
          icon={
            <e.icon
              fontSize="small"
              style={{
                marginLeft: "10px",
                color:
                  view == e.id
                    ? "White"
                    : metaDataValue.branding.primaryColor,
              }}
            />
          }
          color="primary"
          variant="outlined"
          style={{
            backgroundColor:
              view == e.id ? metaDataValue.branding.primaryColor : "",
            height: "30px",
            borderRadius: "7px",
            padding: "6px",
            fontSize: "16px",
            minWidth: "60px",
            color:
              view == e.id ? "White" : metaDataValue.branding.primaryColor,
          }}
          onClick={() => {
            setView(e.id);
          }}
          clickable
        />
      ))}
    </div>
  );

  if (roleLoaded && !holdsAlarmsFeature) {
    return <Redirect to="/solutions/catalogue" />;
  }
  // Report scoping: the popup offers only SR-eligible solutions ("All" = that
  // set). ⊤ sees everything; tolerant-absent keeps pre-migration roles whole.
  const srSolutions = metaDataValue.unscopedAdmin
    ? metaDataValue.services
    : metaDataValue.services.filter(
        (s) => metaDataValue.srEligible?.[String(s.id)] !== false
      );
  return (
    <>
      {schedReportsPerm && (
        <>
          <Drawer anchor={"right"} open={drawer} onClose={toggleDrawer}>
            <div style={{ margin: "20px 20px 10px 20px", overflow: "hidden" }}>
              <Reports
                solutions={[{ name: "All", id: "All" }, ...srSolutions]}
                permission={schedReportsPerm}
              />
            </div>
          </Drawer>
          <Dragable bottom={"30px"} right={"30px"} name="add-operations">
            <Tooltip
              title="Scheduled Reports"
              placement="left"
              arrow
              TransitionComponent={Zoom}
            >
              <Fab
                style={{ boxShadow: "none" }}
                color="secondary"
                onClick={toggleDrawer}
              >
                <ScheduleOutlinedIcon />
              </Fab>
            </Tooltip>
          </Dragable>
        </>
      )}
      {/* Standalone, the page is a column filling its parent (calc(100vh -
          64px), layouts/alarmsDashboard.js) and the List or Timeline takes the
          height left under the filters and charts. Embedded with a serviceId
          (the ESB wrapper has no height), the fixed heights stay. */}
      <div
        style={
          props.serviceId
            ? { overflow: "hidden" }
            : {
                overflow: "hidden",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }
        }
      >
        <AlarmFilters
          priorities={priorities}
          statuses={statuses}
          solutions={metaDataValue.services}
          filters={ alarmsFilter }
          serviceId={props.serviceId}
          origin={origin}
          view={view}
        />
        {/* Timeline view: the raises chart in place of the count cards and
            the trend, at the same height */}
        {view == "timeline" ? (
          <RaisesChart serviceId={props.serviceId} />
        ) : (
          <div style={{ display: "flex" }}>
            {priorityChart()}
            {statusChart()}
            <RaisesChart serviceId={props.serviceId} list />
          </div>
        )}
        <div
          style={{
            backgroundColor: "white",
            paddingBottom: "1px",
            borderRadius: "20px",
            ...(props.serviceId
              ? { height: "calc(100vh - 358px)" }
              : { flex: 1, minHeight: 0 }),
          }}
        >
          {view == "timeline" ? (
            <Timeline
              serviceId={props.serviceId}
              viewToggle={viewToggle}
              fill={!props.serviceId}
            />
          ) : (
            <List
              allAlarms={allAlarms}
              generateRows={generateRows}
              csvData={csvData}
              serviceId={props.serviceId}
              viewToggle={viewToggle}
              fill={!props.serviceId}
            />
          )}
        </div>
      </div>
    </>
  );
}
